import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Shield, Check, X, Clock, User, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: "Pending", icon: Clock, color: "text-yellow-400", bg: "bg-yellow-400/10 border-yellow-400/30" },
  verified: { label: "Verified", icon: Check, color: "text-green-400", bg: "bg-green-400/10 border-green-400/30" },
  rejected: { label: "Rejected", icon: X, color: "text-red-400", bg: "bg-red-400/10 border-red-400/30" },
};

export default function AdminVerificationsPage() {
  const { user } = useAuth();
  const showToast = useToast();
  const { t } = useTranslation();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [note, setNote] = useState("");

  const fetchVerifications = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/verifications`, { withCredentials: true });
      setVerifications(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") fetchVerifications();
  }, [user]);

  const handleReview = async (userId, action) => {
    setReviewingId(userId);
    try {
      const { data } = await axios.put(
        `${API}/admin/verifications/${userId}`,
        { action, note },
        { withCredentials: true }
      );
      setVerifications(prev =>
        prev.map(v => v.id === userId ? { ...v, photo_verification_status: data.photo_verification_status } : v)
      );
      showToast(`User ${action === "approve" ? "verified" : "rejected"}!`);
      setNote("");
    } catch (e) {
      showToast("Review action failed", "error");
    } finally {
      setReviewingId(null);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-slate-600 mb-4" />
          <h1 className="text-white text-xl font-bold mb-2">Admin Access Required</h1>
          <Link to="/login" className="text-brand-orange hover:underline">Sign In</Link>
        </div>
      </div>
    );
  }

  if (user.role !== "admin") {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h1 className="text-white text-xl font-bold mb-2">Access Denied</h1>
          <p className="text-gray-500 text-sm">Admin privileges required.</p>
        </div>
      </div>
    );
  }

  const pending = verifications.filter(v => v.photo_verification_status === "pending");
  const reviewed = verifications.filter(v => v.photo_verification_status !== "pending");

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="bg-white border-b border-gray-100 py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-heading font-black text-brand-navy flex items-center gap-2">
            <Shield size={28} className="text-brand-orange" /> {t("verification.title")}
          </h1>
          <p className="text-gray-500 text-sm mt-1">Review user photo verification requests</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-brand-orange border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Pending */}
            <section className="mb-8">
              <h2 className="text-lg font-bold text-brand-navy mb-4 flex items-center gap-2">
                <Clock size={16} className="text-yellow-400" />
                {t("verification.pending")}
                <span className="ml-1 bg-yellow-400/20 text-yellow-400 text-xs px-2 py-0.5 rounded-full">{pending.length}</span>
              </h2>
              {pending.length === 0 ? (
                <p className="text-gray-500 text-sm">No pending verifications.</p>
              ) : (
                <div className="space-y-4">
                  {pending.map(u => (
                    <div key={u.id} className="bg-white border border-gray-200 rounded-xl p-5" data-testid="verification-card">
                      <div className="flex items-start gap-4">
                        {/* User avatar */}
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt={u.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <User size={24} className="text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-bold text-brand-navy">{u.name}</h3>
                          <p className="text-xs text-gray-500">{u.city}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            Submitted: {u.photo_verification_submitted_at?.slice(0, 10) || "Unknown"}
                          </p>
                        </div>
                        {/* Status badge */}
                        <div className="flex-shrink-0">
                          <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_CONFIG.pending.bg} ${STATUS_CONFIG.pending.color}`}>
                            Pending
                          </span>
                        </div>
                      </div>

                      {/* Verification photo */}
                      {u.photo_verification_url && (
                        <div className="mt-4">
                          <p className="text-xs text-gray-500 mb-2">Submitted photo:</p>
                          <img
                            src={u.photo_verification_url}
                            alt="Verification photo"
                            className="w-40 h-40 object-cover rounded-xl border border-gray-200"
                            data-testid="verification-photo"
                          />
                        </div>
                      )}

                      {/* Note input */}
                      <div className="mt-4">
                        <input
                          placeholder="Optional note to user..."
                          value={reviewingId === u.id ? note : ""}
                          onChange={e => setNote(e.target.value)}
                          onFocus={() => setReviewingId(u.id)}
                          className="w-full bg-navy border border-gray-200 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 dark-section"
                          data-testid="review-note-input"
                        />
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={() => handleReview(u.id, "approve")}
                          disabled={reviewingId === u.id}
                          className="flex items-center gap-1.5 px-4 py-2 bg-green-500/20 text-green-400 border border-green-400/30 rounded-lg text-sm font-medium hover:bg-green-500/30 transition-colors disabled:opacity-50"
                          data-testid="approve-btn"
                        >
                          <Check size={14} /> {t("verification.approve")}
                        </button>
                        <button
                          onClick={() => handleReview(u.id, "reject")}
                          disabled={reviewingId === u.id}
                          className="flex items-center gap-1.5 px-4 py-2 bg-red-500/20 text-red-400 border border-red-400/30 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50"
                          data-testid="reject-btn"
                        >
                          <X size={14} /> {t("verification.reject")}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Reviewed */}
            {reviewed.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-brand-navy mb-4">Recently Reviewed ({reviewed.length})</h2>
                <div className="space-y-3">
                  {reviewed.map(u => {
                    const cfg = STATUS_CONFIG[u.photo_verification_status] || STATUS_CONFIG.pending;
                    const Icon = cfg.icon;
                    return (
                      <div key={u.id} className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-4">
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt={u.name} className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                            <User size={18} className="text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-white text-sm">{u.name}</p>
                          <p className="text-xs text-gray-400">{u.city}</p>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full border flex items-center gap-1 ${cfg.bg} ${cfg.color}`}>
                          <Icon size={10} />
                          {cfg.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
